import { cx } from '../lib/cx'
import { icons, type IconName } from './icons'

export type { IconName } from './icons'

export interface IconProps {
  name: IconName
  /**
   * What the icon means, for screen readers. Leave it out for decorative icons
   * (next to a text label), which are then hidden from assistive technology.
   */
  label?: string
  /** Size in px; 24 by default (PRD: Material Symbols Rounded at 24 dp). */
  size?: number
  className?: string
}

interface ParsedSvg {
  viewBox: string
  body: string
}

const cache = new Map<IconName, ParsedSvg>()

function parse(name: IconName): ParsedSvg {
  let parsed = cache.get(name)
  if (!parsed) {
    const raw: string = icons[name]
    parsed = {
      viewBox: /viewBox="([^"]+)"/.exec(raw)?.[1] ?? '0 -960 960 960',
      body: raw.replace(/^[\s\S]*?<svg[^>]*>/, '').replace(/<\/svg>\s*$/, ''),
    }
    cache.set(name, parsed)
  }
  return parsed
}

/** One Material Symbols icon, drawn in the current text colour. */
export function Icon({ name, label, size = 24, className }: IconProps) {
  const { viewBox, body } = parse(name)
  const a11y = label ? { role: 'img', 'aria-label': label } : { 'aria-hidden': true as const }
  return (
    <svg
      className={cx('icon', className)}
      viewBox={viewBox}
      width={size}
      height={size}
      fill="currentColor"
      focusable="false"
      data-icon={name}
      {...a11y}
      // The markup comes from the icon package at build time, never from people.
      dangerouslySetInnerHTML={{ __html: body }}
    />
  )
}
